import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useRef, useState } from "react";
import { Paperclip, X } from "react-feather";
import { Button } from "risa-oasis-ui_v2";
import {
  ATTACHMENT_ACCEPT,
  isValidSupportTicketAttachment,
  SUPPORTED_ATTACHMENT_COPY,
} from "../utils/validateSupportTicketAttachment";

/**
 * Props for {@link SupportTicketCommentComposer}.
 */
interface SupportTicketCommentComposerProps {
  onAddComment?: (comment: string, files: File[]) => Promise<boolean>;
  isAddingComment?: boolean;
}

/**
 * Comment composer with inline multi-file attachment staging and submit action.
 * Files are staged (not uploaded) until the comment is sent, so text and
 * attachments go together in one action (WhatsApp-style).
 *
 * @param props - Submit callback and in-flight flag.
 * @returns Textarea, staged-file chips, attachment button, and send action.
 */
const SupportTicketCommentComposer = ({
  onAddComment,
  isAddingComment = false,
}: SupportTicketCommentComposerProps) => {
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (selected: FileList | null) => {
    if (!selected) {
      return;
    }
    const valid: File[] = [];
    Array.from(selected).forEach((file) => {
      if (isValidSupportTicketAttachment(file)) {
        valid.push(file);
      } else {
        toast.error(
          `"${file.name}" is not supported. ${SUPPORTED_ATTACHMENT_COPY}.`,
        );
      }
    });
    if (valid.length) {
      setFiles((current) => [...current, ...valid]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_, i) => i !== index));
  };

  const handleAddComment = async () => {
    const trimmedDraft = draft.trim();
    if ((!trimmedDraft && files.length === 0) || isAddingComment) {
      return;
    }

    const wasAdded = await onAddComment?.(trimmedDraft, files);
    if (wasAdded) {
      setDraft("");
      setFiles([]);
    }
  };

  const isSubmitDisabled =
    (draft.trim().length === 0 && files.length === 0) || isAddingComment;

  return (
    <div className="support-tickets-comment-composer flex shrink-0 flex-col gap-2 border-t border-primaryGray-14 px-4 py-3">
      <textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Write your comment"
        rows={4}
        disabled={isAddingComment}
        className="support-tickets-comment-composer__input min-h-[6.5rem] w-full resize-none rounded border border-primaryGray-14 px-3 py-3 text-tiny font-normal leading-[1.125rem] tracking-[0.0125rem] text-primaryGray-2 outline-none placeholder:text-primaryGray-7 disabled:cursor-not-allowed disabled:bg-primaryGray-16"
      />

      {files.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-primaryGray-15 px-2 py-1 text-xs text-primaryGray-3"
              title={file.name}
            >
              <Paperclip className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="min-w-0 truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                disabled={isAddingComment}
                className="shrink-0 text-primaryGray-7 hover:text-primaryGray-4 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={(event) => addFiles(event.target.files)}
        />
        <button
          type="button"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-primaryGray-7 transition-colors hover:bg-primaryGray-15 hover:text-primaryGray-5 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Attach file"
          disabled={isAddingComment}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="h-4 w-4" aria-hidden="true" />
        </button>

        <div className="min-w-0 flex-1 [&>button]:w-full">
          <Button
            buttonType="primary"
            size="medium"
            onClick={() => {
              void handleAddComment();
            }}
            disabled={isSubmitDisabled}
          >
            {isAddingComment ? "Sending..." : "Add Comment"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketCommentComposer;
